
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Coins, MapPin, Phone, Truck, Star, Hammer } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

const AchatFerraille = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Achat Ferraille - Rachat Fer, Acier, Fonte | Groupe Fabre"
        description="Rachat de ferraille au meilleur prix : fer, acier, fonte, métaux ferreux. Pesée certifiée et paiement immédiat."
        canonical="/nos-services/achat-metaux/ferraille"
        keywords="achat ferraille, rachat fer, acier, fonte, métaux ferreux, prix ferraille"
      />
      <Navbar />
      
      <main className="flex-1 mt-16">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center mb-4">
                <Hammer className="h-12 w-12 mr-4" />
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold">Achat de Ferraille</h1>
                  <p className="text-xl mt-2 opacity-90">Rachat de métaux ferreux au meilleur prix</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-fabre-gray mb-6">
                  Rachat de ferraille - Métaux ferreux
                </h2>
                <p className="text-gray-600 mb-6">
                  La <strong>ferraille</strong> représente une ressource précieuse dans l'économie circulaire. 
                  <strong>Groupe Fabre Environnement</strong> rachète tous types de métaux ferreux : 
                  fer, acier, fonte et alliages ferreux au meilleur prix du marché.
                </p>
                
                <h3 className="text-xl font-bold text-fabre-gray mb-4">Types de ferraille rachetés</h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-700 text-white mr-3">•</span>
                    <span><strong>Fer :</strong> poutres, profilés, tubes, tôles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-700 text-white mr-3">•</span>
                    <span><strong>Acier :</strong> charpentes, machines, outils</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-700 text-white mr-3">•</span>
                    <span><strong>Fonte :</strong> radiateurs, pièces industrielles</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-700 text-white mr-3">•</span>
                    <span><strong>Fer à béton :</strong> armatures, treillis</span>
                  </li>
                </ul>

                <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg">
                  <h4 className="font-bold text-gray-800 mb-3">Avantages du recyclage ferraille</h4>
                  <ul className="text-gray-700 text-sm space-y-2">
                    <li>• Économie d'énergie de 75% vs production primaire</li>
                    <li>• Réduction des émissions de CO2</li>
                    <li>• Recyclage infini sans perte de qualité</li>
                    <li>• Valorisation économique optimale</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-2xl font-bold text-fabre-gray mb-6">Valorisez votre ferraille</h3>
                <p className="text-gray-600 mb-6">
                  Nos experts évaluent la qualité de votre ferraille et vous proposent 
                  les meilleurs prix selon les cours du marché.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center">
                    <Coins className="h-5 w-5 text-gray-700 mr-3" />
                    <span>Prix selon cours LME</span>
                  </div>
                  <div className="flex items-center">
                    <Hammer className="h-5 w-5 text-gray-700 mr-3" />
                    <span>Pesée certifiée</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-gray-700 mr-3" />
                    <span>Paiement immédiat</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <button className="w-full px-6 py-3 bg-gray-700 text-white rounded-md font-medium hover:bg-gray-800 transition-colors">
                    Demander prix ferraille
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Types de ferraille */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-fabre-gray mb-8 text-center">
              Classifications ferraille et tarifs
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Ferraille Lourde</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Poutres, rails, pièces épaisses ({'>'}6mm)
                </p>
                <div className="text-green-600 font-medium">Prix le plus élevé</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Ferraille Légère</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Tôles fines, emballages, structures légères
                </p>
                <div className="text-orange-600 font-medium">Prix standard</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-bold text-gray-700 mb-3">Ferraille Mélangée</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Mélange de différentes qualités de fer
                </p>
                <div className="text-blue-600 font-medium">Prix moyen</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AchatFerraille;
